﻿import { useState, useRef } from "react"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { type AvatarFolder, type AvatarAsset } from "@/data/adminData"
import { adminAvatarsApi } from "@/lib/api"
import { ChevronRight, Folder, FolderOpen, Plus, Upload, Lock, MoreHorizontal, FolderPlus, Trash2, Pencil, Check, X } from "lucide-react"

function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button onClick={() => onChange(!on)} style={{ width:36, height:20, borderRadius:10, border:'none', background: on ? 'oklch(0.145 0 0)' : 'oklch(0.84 0 0)', position:'relative', cursor:'pointer', transition:'background 0.2s', flexShrink:0, padding:0 }}>
      <span style={{ position:'absolute', top:2, left: on ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'white', transition:'left 0.18s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )
}

type Props = {
  folders: AvatarFolder[]
  assets: AvatarAsset[]
  onFoldersChange: (f: AvatarFolder[]) => void
  onAssetsChange: (a: AvatarAsset[]) => void
}

function FolderNode({ folder, folders, assets, depth, selected, onSelect, onDelete, onTogglePrivate, onDropAsset, onRename, onMoveFolder, parentPrivate = false }: {
  folder: AvatarFolder; folders: AvatarFolder[]; assets: AvatarAsset[]
  depth: number; selected: string | null
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onTogglePrivate: (id: string) => void
  onDropAsset: (assetId: string, folderId: string) => void
  onRename: (id: string, name: string) => void
  onMoveFolder: (id: string, newParentId: string | null) => void
  parentPrivate?: boolean
}) {
  const [open, setOpen] = useState(true)
  const [dropOver, setDropOver] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editVal, setEditVal] = useState(folder.name)
  const inputRef = useRef<HTMLInputElement>(null)
  const children = folders.filter(f => f.parentId === folder.id)
  const getDescFolderIds = (id: string): string[] => {
    const ch = folders.filter(f => f.parentId === id)
    return [id, ...ch.flatMap(c => getDescFolderIds(c.id))]
  }
  const assetCount = assets.filter(a => a.folderId !== null && getDescFolderIds(folder.id).includes(a.folderId)).length
  const isSelected = selected === folder.id
  const isPrivate = folder.private || parentPrivate

  const startEdit = (e: React.MouseEvent) => { e.stopPropagation(); setEditVal(folder.name); setEditing(true); setTimeout(() => inputRef.current?.select(), 10) }
  const commitEdit = () => { const v = editVal.trim(); if(v && v !== folder.name) onRename(folder.id, v); setEditing(false) }
  const cancelEdit = () => { setEditing(false); setEditVal(folder.name) }

  // prevent dropping a folder onto its own descendant
  const isDescendant = (targetId: string): boolean => {
    if (targetId === folder.id) return true
    return folders.filter(f => f.parentId === folder.id).some(c => {
      const check = (id: string): boolean => id === targetId || folders.filter(f => f.parentId === id).some(cc => check(cc.id))
      return check(c.id)
    })
  }

  return (
    <div>
      <div
        className={["flex items-center gap-1 py-1 rounded-md cursor-pointer group select-none transition-colors", isSelected ? "bg-secondary" : dropOver ? "bg-blue-100 ring-1 ring-blue-400" : "hover:bg-secondary/60"].join(" ")}
        style={{ paddingLeft: `${8 + depth * 16}px`, paddingRight: 6 }}
        draggable={!editing}
        onDragStart={e => { e.stopPropagation(); e.dataTransfer.setData("folderId", folder.id) }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDropOver(true) }}
        onDragLeave={() => setDropOver(false)}
        onDrop={e => {
          e.preventDefault(); e.stopPropagation(); setDropOver(false)
          const assetId = e.dataTransfer.getData("assetId")
          const folderId = e.dataTransfer.getData("folderId")
          if (assetId) onDropAsset(assetId, folder.id)
          else if (folderId && folderId !== folder.id && !isDescendant(folderId)) onMoveFolder(folderId, folder.id)
        }}
      >
        {children.length > 0 ? (
          <button onClick={() => setOpen(o => !o)} className="text-muted-foreground hover:text-foreground flex-shrink-0 p-0.5">
            <ChevronRight className={["w-3 h-3 transition-transform", open ? "rotate-90" : ""].join(" ")} />
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}
        {editing ? (
          <div className="flex items-center gap-1 flex-1 min-w-0" onClick={e => e.stopPropagation()}>
            <input ref={inputRef} value={editVal} onChange={e => setEditVal(e.target.value)}
              onKeyDown={e => { if(e.key==='Enter') commitEdit(); if(e.key==='Escape') cancelEdit() }}
              className="flex-1 min-w-0 text-xs bg-background border border-foreground/30 rounded px-1.5 py-0.5 outline-none"
              autoFocus
            />
            <button onClick={commitEdit} className="p-0.5 text-green-600 hover:text-green-700"><Check className="w-3 h-3" /></button>
            <button onClick={cancelEdit} className="p-0.5 text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <span onClick={() => onSelect(folder.id)} className="flex items-center gap-1.5 flex-1 min-w-0 py-0.5">
            {isPrivate
              ? <Lock className="w-3.5 h-3.5 flex-shrink-0 text-orange-500" />
              : isSelected && open
                ? <FolderOpen className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
                : <Folder className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
            }
            <span className={["text-xs truncate", isPrivate ? "text-orange-600 font-medium" : ""].join(" ")}>{folder.name}</span>
            {assetCount > 0 && <span className="text-[10px] text-muted-foreground ml-auto pl-1 flex-shrink-0">{assetCount}</span>}
          </span>
        )}
        {!editing && (
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 flex-shrink-0 transition-opacity">
            <button onClick={startEdit} className="p-0.5 text-muted-foreground hover:text-foreground rounded transition-colors">
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => onTogglePrivate(folder.id)} title={folder.private ? "Make public" : "Make private"}
              className={["p-0.5 rounded transition-colors", isPrivate ? "text-orange-500 hover:text-orange-600" : "text-muted-foreground hover:text-foreground"].join(" ")}>
              <Lock className="w-3 h-3" />
            </button>
            <button onClick={() => onDelete(folder.id)} className="p-0.5 text-muted-foreground hover:text-destructive rounded transition-colors">
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      {open && children.map(c => (
        <FolderNode key={c.id} folder={c} folders={folders} assets={assets} depth={depth + 1} selected={selected} onSelect={onSelect} onDelete={onDelete} onTogglePrivate={onTogglePrivate} onDropAsset={onDropAsset} onRename={onRename} onMoveFolder={onMoveFolder} parentPrivate={isPrivate} />
      ))}
    </div>
  )
}

export function AvatarsPage({ folders, assets, onFoldersChange, onAssetsChange }: Props) {
  const [selected, _setSelected] = useState<string | null>(null)
  const setSelected = (v: string | null) => { _setSelected(v); setActiveTags([]) }
  const [createModal, setCreateModal] = useState(false)
  const [uploadModal, setUploadModal] = useState(false)
  const [uploadTarget, setUploadTarget] = useState<string | null>(null)
  const [folderName, setFolderName] = useState("")
  const [folderPrivate, setFolderPrivate] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [contextAsset, setContextAsset] = useState<AvatarAsset | null>(null)
  const [assetMenu, setAssetMenu] = useState(false)
  const [moveModal, setMoveModal] = useState(false)
  const [activeTags, setActiveTags] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  const getTagPath = (folderId: string | null): string[] => {
    const path: string[] = []
    let node = folders.find(f => f.id === folderId)
    while (node) {
      path.unshift(node.name)
      node = folders.find(f => f.id === node!.parentId)
    }
    return path
  }

  const toggleTag = (tag: string) => {
    setActiveTags(ts => ts.includes(tag) ? ts.filter(t => t !== tag) : [...ts, tag])
  }

  const currentFolder = folders.find(f => f.id === selected) ?? null
  const breadcrumb: AvatarFolder[] = []
  if (currentFolder) {
    let node: AvatarFolder | undefined = currentFolder
    while (node) {
      breadcrumb.unshift(node)
      node = folders.find(f => f.id === node!.parentId)
    }
  }

  const isInPrivateFolder = (folderId: string | null): boolean => {
    if (!folderId) return false
    const f = folders.find(x => x.id === folderId)
    if (!f) return false
    if (f.private) return true
    return isInPrivateFolder(f.parentId)
  }

  const getDescendantIds = (folderId: string): string[] => {
    const children = folders.filter(f => f.parentId === folderId)
    return [folderId, ...children.flatMap(c => getDescendantIds(c.id))]
  }
  const poolAssets = selected === null ? assets : assets.filter(a => a.folderId !== null && getDescendantIds(selected).includes(a.folderId))
  const visibleAssets = activeTags.length === 0
    ? poolAssets
    : poolAssets.filter(a => {
        const tags = getTagPath(a.folderId)
        return activeTags.every(t => tags.includes(t))
      })

  // all unique tags across current pool
  const allTags = Array.from(new Set(poolAssets.flatMap(a => getTagPath(a.folderId)))).sort()

  const subfolders = folders.filter(f => f.parentId === selected)
  const rootFolders = folders.filter(f => f.parentId === null)

  const createFolder = async () => {
    const name = folderName.trim()
    if (!name) return
    const dto = await adminAvatarsApi.createFolder({ name, parent_id: selected ? Number(selected) : null, is_private: folderPrivate }).catch(() => null)
    if (!dto) return
    onFoldersChange([...folders, { id: String(dto.id), name: dto.name, parentId: dto.parent_id ? String(dto.parent_id) : null, private: dto.is_private }])
    setFolderName("")
    setFolderPrivate(false)
    setCreateModal(false)
  }

  const deleteFolder = async (id: string) => {
    const descendants = (fid: string): string[] => {
      const kids = folders.filter(f => f.parentId === fid).map(f => f.id)
      return [fid, ...kids.flatMap(descendants)]
    }
    const toRemove = descendants(id)
    await adminAvatarsApi.deleteFolder(Number(id)).catch(() => {})
    onFoldersChange(folders.filter(f => !toRemove.includes(f.id)))
    onAssetsChange(assets.map(a => toRemove.includes(a.folderId ?? "") ? { ...a, folderId: null } : a))
    if (selected && toRemove.includes(selected)) setSelected(null)
  }

  const toggleFolderPrivate = async (id: string) => {
    const target = folders.find(f => f.id === id)
    if (!target) return
    const makingPrivate = !target.private
    const dto = await adminAvatarsApi.updateFolder(Number(id), { is_private: makingPrivate }).catch(() => null)
    if (!dto) return

    if (makingPrivate) {
      const descendants = (fid: string): string[] => {
        const kids = folders.filter(f => f.parentId === fid).map(f => f.id)
        return [...kids, ...kids.flatMap(descendants)]
      }
      const descIds = descendants(id)
      onFoldersChange(folders.map(f => f.id === id || descIds.includes(f.id) ? { ...f, private: true } : f))
      onAssetsChange(assets.map(a => descIds.includes(a.folderId ?? '') || a.folderId === id ? { ...a, private: true } : a))
    } else {
      onFoldersChange(folders.map(f => f.id === id ? { ...f, private: false } : f))
    }
  }

  const renameFolder = async (id: string, name: string) => {
    await adminAvatarsApi.updateFolder(Number(id), { name }).catch(() => {})
    onFoldersChange(folders.map(f => f.id === id ? { ...f, name } : f))
  }

  const moveFolderParent = async (id: string, newParentId: string | null) => {
    const newParent = folders.find(f => f.id === newParentId)
    const parentIsPrivate = newParent ? (newParent.private || isInPrivateFolder(newParent.parentId)) : false
    await adminAvatarsApi.updateFolder(Number(id), { parent_id: newParentId ? Number(newParentId) : null }).catch(() => {})
    const updatedFolders = folders.map(f => f.id === id ? { ...f, parentId: newParentId } : f)
    if (parentIsPrivate) {
      const descendants = (fid: string): string[] => {
        const kids = updatedFolders.filter(f => f.parentId === fid).map(f => f.id)
        return [...kids, ...kids.flatMap(descendants)]
      }
      const affectedFolderIds = [id, ...descendants(id)]
      onFoldersChange(updatedFolders.map(f => affectedFolderIds.includes(f.id) ? { ...f, private: true } : f))
      onAssetsChange(assets.map(a => affectedFolderIds.includes(a.folderId ?? '') ? { ...a, private: true } : a))
    } else {
      onFoldersChange(updatedFolders)
    }
  }

  const toggleAssetPrivate = async (id: string) => {
    const asset = assets.find(a => a.id === id)
    if (!asset) return
    await adminAvatarsApi.updateAsset(Number(id), { is_private: !asset.private }).catch(() => {})
    onAssetsChange(assets.map(a => a.id === id ? { ...a, private: !a.private } : a))
  }

  const handleFiles = async (files: FileList | null, targetFolder: string | null) => {
    if (!files) return
    const folderId = targetFolder ? Number(targetFolder) : null
    const newAssets: AvatarAsset[] = []
    for (const file of Array.from(files)) {
      const dto = await adminAvatarsApi.uploadAsset(file, folderId).catch(() => null)
      if (dto) {
        newAssets.push({
          id: String(dto.id),
          name: dto.name,
          img: dto.img_path,
          folderId: dto.folder_id ? String(dto.folder_id) : null,
          private: dto.is_private,
        })
      }
    }
    if (newAssets.length) onAssetsChange([...assets, ...newAssets])
  }

  const triggerUpload = () => {
    if (selected !== null) {
      fileRef.current?.click()
    } else {
      setUploadTarget(null)
      setUploadModal(true)
    }
  }

  const deleteAsset = async (id: string) => {
    await adminAvatarsApi.deleteAsset(Number(id)).catch(() => {})
    onAssetsChange(assets.filter(a => a.id !== id))
    setAssetMenu(false)
    setContextAsset(null)
  }

  const moveAsset = async (assetId: string, toFolderId: string) => {
    const targetPrivate = isInPrivateFolder(toFolderId) || (folders.find(f => f.id === toFolderId)?.private ?? false)
    await adminAvatarsApi.updateAsset(Number(assetId), { folder_id: Number(toFolderId) }).catch(() => {})
    onAssetsChange(assets.map(a => a.id === assetId
      ? { ...a, folderId: toFolderId, private: targetPrivate ? true : a.private }
      : a))
    setMoveModal(false)
    setContextAsset(null)
  }

  const assetIsEffectivelyPrivate = (av: AvatarAsset) => av.private || isInPrivateFolder(av.folderId)

  return (
    <div className="flex gap-4 h-[calc(100vh-8rem)]">

      {/* Folder tree */}
      <Card className="w-56 flex-shrink-0 flex flex-col overflow-hidden">
        <div className="px-3 pt-3 pb-2 border-b flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Folders</span>
          <button onClick={() => setCreateModal(true)} className="text-muted-foreground hover:text-foreground transition-colors">
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-1.5 px-1">
          <div
            onClick={() => setSelected(null)}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); const fid = e.dataTransfer.getData("folderId"); if(fid) moveFolderParent(fid, null) }}
            className={["flex items-center gap-1.5 py-1 px-2 rounded-md cursor-pointer text-xs transition-colors", selected === null ? "bg-secondary" : "hover:bg-secondary/60"].join(" ")}
          >
            <Folder className="w-3.5 h-3.5 text-muted-foreground" />
            <span>All avatars</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{assets.length}</span>
          </div>
          {rootFolders.map(f => (
            <FolderNode key={f.id} folder={f} folders={folders} assets={assets} depth={0} selected={selected} onSelect={setSelected} onDelete={deleteFolder} onTogglePrivate={toggleFolderPrivate} onDropAsset={moveAsset} onRename={renameFolder} onMoveFolder={moveFolderParent} />
          ))}
        </div>
      </Card>

      {/* Content area */}
      <div className="flex-1 flex flex-col gap-3 min-w-0">

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs min-w-0">
            <button onClick={() => setSelected(null)} className="text-muted-foreground hover:text-foreground transition-colors">Avatars</button>
            {breadcrumb.map((f, i) => (
              <span key={f.id} className="flex items-center gap-1">
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                <button
                  onClick={() => setSelected(f.id)}
                  className={i === breadcrumb.length - 1 ? "font-medium" : "text-muted-foreground hover:text-foreground transition-colors"}
                >{f.name}</button>
              </span>
            ))}
            {currentFolder?.private && (
              <span className="ml-2 flex items-center gap-1 text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-semibold">
                <Lock className="w-2.5 h-2.5" />Admin only
              </span>
            )}
          </div>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={triggerUpload}>
              <Upload className="w-3.5 h-3.5" />Upload
            </Button>
            <Button size="sm" className="h-8 gap-1.5 text-xs rounded-full" onClick={() => setCreateModal(true)}>
              <Plus className="w-3.5 h-3.5" />New folder
            </Button>
          </div>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={e => { handleFiles(e.target.files, selected); e.target.value = "" }} />
        </div>

        {/* Tag filter bar */}
        {allTags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Tags</span>
            {allTags.map(tag => (
              <button key={tag} onClick={() => toggleTag(tag)}
                className={["text-[11px] px-2.5 py-0.5 rounded-full border font-medium transition-colors", activeTags.includes(tag) ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary text-muted-foreground"].join(" ")}>
                {tag}
              </button>
            ))}
            {activeTags.length > 0 && (
              <button onClick={() => setActiveTags([])} className="text-[11px] text-muted-foreground hover:text-foreground underline ml-1 transition-colors">clear</button>
            )}
          </div>
        )}

        {/* Drop zone + grid */}
        <Card
          className={["flex-1 overflow-y-auto p-4 transition-colors", dragOver && selected ? "ring-2 ring-foreground/30 bg-secondary/40" : ""].join(" ")}
          onDragOver={e => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files, selected) }}
        >
          {subfolders.length === 0 && visibleAssets.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center gap-3 text-muted-foreground">
              <Upload className="w-8 h-8 opacity-20" />
              <span className="text-sm">Drop images here or click Upload</span>
            </div>
          )}

          {/* Subfolders */}
          {subfolders.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Folders</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {subfolders.map(f => {
                  const count = assets.filter(a => a.folderId === f.id).length
                  return (
                    <button key={f.id} onClick={() => setSelected(f.id)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); const id = e.dataTransfer.getData("assetId"); if(id) moveAsset(id, f.id) }}
                      className={["flex flex-col items-center gap-2 p-3 rounded-xl border transition-colors", (f.private || isInPrivateFolder(f.parentId)) ? "border-orange-200 bg-orange-50/50 hover:bg-orange-50" : "border-border hover:border-foreground/30 hover:bg-secondary/40"].join(" ")}>
                      {(f.private || isInPrivateFolder(f.parentId))
                        ? <Lock className="w-8 h-8 text-orange-400" />
                        : <FolderOpen className="w-8 h-8 text-muted-foreground" />
                      }
                      <span className={["text-xs font-medium truncate w-full text-center", (f.private || isInPrivateFolder(f.parentId)) ? "text-orange-600" : ""].join(" ")}>{f.name}</span>
                      <span className="text-[10px] text-muted-foreground">{count} file{count !== 1 ? "s" : ""}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Assets in folder */}
          {visibleAssets.length > 0 && (
            <div>
              {subfolders.length > 0 && <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 mt-4">Files</div>}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {visibleAssets.map(av => {
                  const effective = assetIsEffectivelyPrivate(av)
                  return (
                    <div key={av.id} draggable onDragStart={e => e.dataTransfer.setData("assetId", av.id)} className={["relative flex flex-col rounded-xl border overflow-hidden transition-colors cursor-grab active:cursor-grabbing", effective ? "border-orange-300" : "border-border hover:border-foreground/30"].join(" ")}>
                      {effective && (
                        <div className="absolute top-1.5 left-1.5 z-10 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center shadow">
                          <Lock className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      <button
                        onClick={() => { setContextAsset(av); setAssetMenu(true) }}
                        className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-white border border-border shadow-sm hover:bg-secondary flex items-center justify-center transition-colors">
                        <MoreHorizontal className="w-3 h-3 text-foreground" />
                      </button>
                      <div className="w-full aspect-[2/3] overflow-hidden bg-secondary">
                        <img src={av.img} alt={av.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-2 flex flex-col items-center gap-1.5">
                        <div className="text-xs font-semibold truncate w-full text-center">{av.name}</div>
                        <div className="flex flex-wrap justify-center gap-1">
                          {getTagPath(av.folderId).map(tag => (
                            <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">#{tag}</span>
                          ))}
                        </div>
                        {!currentFolder?.private && (
                          <div className="flex flex-col items-center gap-1 w-full">
                            <span className={["text-[10px] font-semibold px-2 py-0.5 rounded-full", effective ? "bg-orange-100 text-orange-600" : "bg-secondary text-muted-foreground"].join(" ")}>
                              {effective ? "Admin only" : "Public"}
                            </span>
                            <Toggle on={av.private} onChange={() => toggleAssetPrivate(av.id)} />
                          </div>
                        )}
                        {currentFolder?.private && (
                          <span className="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-semibold">Admin only</span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        </Card>
      </div>

      {/* Create folder modal */}
      <Dialog open={createModal} onOpenChange={v => { setCreateModal(v); if(!v){ setFolderName(""); setFolderPrivate(false) } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{selected ? `New subfolder in "${currentFolder?.name}"` : "New folder"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-1">
            <Input autoFocus placeholder="Folder name" value={folderName} onChange={e => setFolderName(e.target.value)} onKeyDown={e => e.key === "Enter" && createFolder()} />
            <div className="flex items-center justify-between px-1 py-2 rounded-lg bg-secondary/60">
              <div className="flex items-center gap-2">
                <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-medium">Admin only</span>
              </div>
              <Toggle on={folderPrivate} onChange={setFolderPrivate} />
            </div>
            {folderPrivate && <p className="text-[11px] text-orange-600 -mt-1">CLI users cannot access this folder or its contents.</p>}
            <div className="flex gap-2">
              <Button className="flex-1" onClick={createFolder}>Create</Button>
              <Button variant="outline" onClick={() => setCreateModal(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload — pick folder modal (when no folder selected) */}
      <Dialog open={uploadModal} onOpenChange={setUploadModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Upload — select destination</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1 max-h-64 overflow-y-auto">
            {folders.map(f => (
              <button key={f.id} onClick={() => { setUploadTarget(f.id); setUploadModal(false); setTimeout(() => fileRef.current?.click(), 50) }}
                className={["flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm", f.private ? "border-orange-200 bg-orange-50/40" : "border-border hover:bg-secondary/60"].join(" ")}>
                {f.private ? <Lock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /> : <Folder className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                <span className={f.private ? "text-orange-600 font-medium" : ""}>{f.name}</span>
                {f.parentId && <span className="text-[10px] text-muted-foreground ml-auto">{folders.find(p => p.id === f.parentId)?.name}</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <input type="file" multiple accept="image/*" className="hidden" onChange={e => { handleFiles(e.target.files, uploadTarget); e.target.value = "" }} />

      {/* Asset action menu */}
      <Dialog open={assetMenu} onOpenChange={v => { setAssetMenu(v); if(!v) setContextAsset(null) }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>{contextAsset?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <button onClick={() => { setAssetMenu(false); setMoveModal(true) }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border hover:bg-secondary/60 transition-colors text-sm text-left">
              <FolderOpen className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              Move to folder
            </button>
            <button onClick={() => contextAsset && deleteAsset(contextAsset.id)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-destructive/30 hover:bg-destructive/10 transition-colors text-sm text-left text-destructive">
              <Trash2 className="w-4 h-4 flex-shrink-0" />
              Delete
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move modal */}
      <Dialog open={moveModal} onOpenChange={v => { setMoveModal(v); if(!v) setContextAsset(null) }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move "{contextAsset?.name}"</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1 max-h-64 overflow-y-auto">
            {folders.map(f => (
              <button key={f.id} onClick={() => contextAsset && moveAsset(contextAsset.id, f.id)}
                className={["flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors text-left text-sm", contextAsset?.folderId === f.id ? "bg-secondary border-foreground/20" : f.private ? "border-orange-200 bg-orange-50/40 hover:bg-orange-50" : "border-border hover:bg-secondary/60"].join(" ")}>
                {f.private ? <Lock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /> : <Folder className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                <span className={f.private ? "text-orange-600 font-medium" : ""}>{f.name}</span>
                {f.parentId && <span className="text-[10px] text-muted-foreground ml-auto">{folders.find(p => p.id === f.parentId)?.name}</span>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}